'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, BellRing, X, Check, CheckCheck, Package, MessageCircle, CheckCircle2, XCircle, Mail, Heart, DollarSign, ExternalLink } from 'lucide-react';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { useRealtimeNotifications, RealtimeNotif, RealtimeNotifType } from '@/hooks/useRealtimeNotifications';

const TYPE_META: Record<RealtimeNotifType, { icon: React.ElementType; color: string; label: string }> = {
  order_update: { icon: Package, color: '#00BCD4', label: 'Order' },
  new_message: { icon: MessageCircle, color: '#7C4DFF', label: 'Message' },
  listing_approved: { icon: CheckCircle2, color: '#00C853', label: 'Approved' },
  listing_rejected: { icon: XCircle, color: '#FF3B5C', label: 'Rejected' },
  new_inquiry: { icon: Mail, color: '#FFB300', label: 'Inquiry' },
  new_match: { icon: Heart, color: '#FF4081', label: 'Match' },
  payment_received: { icon: DollarSign, color: '#00C853', label: 'Payment' },
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function NotificationBell() {
  const { user } = useAuth();
  const supabase = createClient();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<RealtimeNotif[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [mounted, setMounted] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setMounted(true); }, []);

  // Check notification permission
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    setPermissionGranted(Notification.permission === 'granted');
  }, []);

  // Load existing notifications from DB
  const loadNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await supabase
        .from('realtime_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(30);
      if (data) {
        setNotifications(data as RealtimeNotif[]);
        setUnread(data.filter((n: any) => !n.is_read).length);
      }
    } catch {}
    setLoading(false);
  }, [user]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Handle incoming realtime notification
  const handleNewNotif = useCallback((notif: RealtimeNotif) => {
    setNotifications(prev => [notif, ...prev].slice(0, 30));
    setUnread(prev => prev + 1);
  }, []);

  // Subscribe to realtime
  const { requestPermission } = useRealtimeNotifications({
    userId: user?.id,
    onNotification: handleNewNotif,
    autoRequestPermission: false,
  });

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markAllRead = async () => {
    if (!user) return;
    await supabase
      .from('realtime_notifications')
      .update({ is_read: true })
      .eq('user_id', user.id)
      .eq('is_read', false);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnread(0);
  };

  const markRead = async (id: string) => {
    await supabase.from('realtime_notifications').update({ is_read: true }).eq('id', id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    setUnread(prev => Math.max(0, prev - 1));
  };

  const enablePush = async () => {
    const granted = await requestPermission();
    setPermissionGranted(granted);
  };

  if (!mounted) return null;

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={() => { setOpen(o => !o); if (!open) loadNotifications(); }}
        className="relative w-9 h-9 rounded-full flex items-center justify-center transition-all"
        style={{ background: open ? 'rgba(0,200,83,0.12)' : 'rgba(255,255,255,0.06)', border: `1px solid ${open ? 'rgba(0,200,83,0.3)' : 'rgba(255,255,255,0.1)'}` }}
        aria-label="Notifications">
        {unread > 0
          ? <BellRing size={17} style={{ color: '#00C853' }} />
          : <Bell size={17} style={{ color: 'rgba(255,255,255,0.6)' }} />}
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full flex items-center justify-center text-xs font-800 px-1"
            style={{ background: '#FF3B5C', color: '#fff', fontSize: '10px' }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 top-12 w-80 rounded-2xl shadow-2xl z-50 overflow-hidden"
          style={{ background: '#0d1117', border: '1px solid rgba(255,255,255,0.1)', maxHeight: '480px' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center gap-2">
              <Bell size={15} style={{ color: '#00C853' }} />
              <span className="text-sm font-700 text-white">Notifications</span>
              {unread > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-xs font-700"
                  style={{ background: 'rgba(255,59,92,0.15)', color: '#FF3B5C' }}>
                  {unread} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button onClick={markAllRead} className="text-xs font-600 flex items-center gap-1"
                  style={{ color: '#00C853' }}>
                  <CheckCheck size={12} /> All read
                </button>
              )}
              <button onClick={() => setOpen(false)}
                className="w-6 h-6 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(255,255,255,0.06)' }}>
                <X size={13} style={{ color: 'rgba(255,255,255,0.5)' }} />
              </button>
            </div>
          </div>

          {/* Push permission banner */}
          {!permissionGranted && (
            <div className="px-4 py-3 flex items-center justify-between gap-3"
              style={{ background: 'rgba(0,200,83,0.06)', borderBottom: '1px solid rgba(0,200,83,0.1)' }}>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                Enable push notifications to stay updated
              </p>
              <button onClick={enablePush}
                className="px-3 py-1.5 rounded-lg text-xs font-700 flex-shrink-0"
                style={{ background: '#00C853', color: '#fff' }}>
                Enable
              </button>
            </div>
          )}

          {/* Notification list */}
          <div className="overflow-y-auto" style={{ maxHeight: '340px' }}>
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <div className="w-6 h-6 rounded-full border-2 animate-spin"
                  style={{ borderColor: 'rgba(0,200,83,0.3)', borderTopColor: '#00C853' }} />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-3">
                <Bell size={28} style={{ color: 'rgba(255,255,255,0.15)' }} />
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const meta = TYPE_META[notif.type] || TYPE_META['new_message'];
                const IconComp = meta.icon;
                return (
                  <div key={notif.id}
                    className="flex items-start gap-3 px-4 py-3 cursor-pointer transition-all"
                    style={{
                      background: notif.is_read ? 'transparent' : 'rgba(0,200,83,0.04)',
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                    }}
                    onClick={() => {
                      if (!notif.is_read) markRead(notif.id);
                      if (notif.url && notif.url !== '/') window.location.href = notif.url;
                      setOpen(false);
                    }}>
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: `${meta.color}18` }}>
                      <IconComp size={15} style={{ color: meta.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-xs font-700 leading-tight" style={{ color: notif.is_read ? 'rgba(255,255,255,0.6)' : '#fff' }}>
                          {notif.title}
                        </p>
                        {!notif.is_read && (
                          <div className="w-2 h-2 rounded-full flex-shrink-0 mt-1" style={{ background: '#00C853' }} />
                        )}
                      </div>
                      <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'rgba(255,255,255,0.4)' }}>
                        {notif.body}
                      </p>
                      <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.25)' }}>
                        {timeAgo(notif.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <Link href="/notifications" onClick={() => setOpen(false)}
              className="flex items-center justify-center gap-2 text-xs font-600 transition-all"
              style={{ color: '#00C853' }}>
              View all notifications <ExternalLink size={11} />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
